/**
 * Cognito authentication service for the HHU.
 *
 * Same OAuth2 authorization-code flow as the Portal, but with HHU-specific
 * storage and a permission-aware user object. When we move to CandyCane +
 * IIB in production, only this file changes — the rest of the app reads
 * from getCurrentUser() and never touches raw tokens.
 */

import type { AuthenticatedUser, Permission } from "src/types/user"
import { PERMISSION } from "src/types/user"

// ─── Config validation ────────────────────────────────────────────────────────

/**
 * Returns the value of a required environment variable, or throws a clear
 * error on first use so a misconfigured deploy is discovered at auth time
 * rather than silently using wrong credentials or falling back to hard-coded
 * values. The check is lazy (not at module import) to keep unit tests that
 * mock authService from needing to set every env var.
 */
function requireEnv(key: string): string {
  const value = import.meta.env[key] as string | undefined
  if (!value) throw new Error(`Missing required environment variable: ${key}`)
  return value
}

interface CognitoConfig {
  userPoolId: string
  clientId: string
  domain: string
  redirectUri: string
  logoutUri: string
  scopes: string[]
  identityProvider: string
}

let _cognitoConfig: CognitoConfig | null = null

/** Lazily initialised — throws on first call if any env var is absent. */
function getCognitoConfig(): CognitoConfig {
  if (_cognitoConfig) return _cognitoConfig
  _cognitoConfig = {
    userPoolId: requireEnv("VITE_COGNITO_USER_POOL_ID"),
    clientId: requireEnv("VITE_COGNITO_CLIENT_ID"),
    domain: requireEnv("VITE_COGNITO_DOMAIN"),
    redirectUri: window.location.origin + "/callback",
    logoutUri: window.location.origin + "/",
    scopes: ["openid", "email", "profile"],
    identityProvider: requireEnv("VITE_COGNITO_IDENTITY_PROVIDER"),
  }
  return _cognitoConfig
}

// ─── Token storage ─────────────────────────────────────────────────────────────

/** Exported so apiClient can reference the same key without duplicating a magic string. */
export const TOKEN_KEY = "eclipse_hhu_tokens"

interface TokenResponse {
  id_token: string
  access_token: string
  refresh_token?: string
  expires_in: number
  token_type: string
}

interface StoredTokens {
  idToken: string
  accessToken: string
  refreshToken?: string
  expiresAt: number
}

function getStoredTokens(): StoredTokens | null {
  const raw = sessionStorage.getItem(TOKEN_KEY)
  if (!raw) return null
  try {
    const tokens: StoredTokens = JSON.parse(raw)
    if (Date.now() > tokens.expiresAt - 60_000) {
      sessionStorage.removeItem(TOKEN_KEY)
      return null
    }
    return tokens
  } catch {
    return null
  }
}

function storeTokens(response: TokenResponse): StoredTokens {
  const tokens: StoredTokens = {
    idToken: response.id_token,
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    expiresAt: Date.now() + response.expires_in * 1000,
  }
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
  return tokens
}

/** Remove tokens from sessionStorage. Call this instead of accessing TOKEN_KEY directly. */
export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEY)
}

export function getAccessToken(): string | null {
  return getStoredTokens()?.accessToken ?? null
}

export function getIdToken(): string | null {
  return getStoredTokens()?.idToken ?? null
}

export function isAuthenticated(): boolean {
  return getStoredTokens() !== null
}

// ─── Silent token refresh ─────────────────────────────────────────────────────

/**
 * Attempts to silently exchange the stored refresh token for a new access
 * token without requiring the user to re-authenticate. Returns the new
 * access token on success, or null when the refresh token is absent or
 * the request fails (callers should then redirect to login).
 */
export async function refreshTokensAsync(): Promise<string | null> {
  const raw = sessionStorage.getItem(TOKEN_KEY)
  if (!raw) return null
  let stored: StoredTokens
  try {
    stored = JSON.parse(raw) as StoredTokens
  } catch {
    return null
  }
  if (!stored.refreshToken) return null

  const config = getCognitoConfig()
  try {
    const response = await fetch(`https://${config.domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: config.clientId,
        refresh_token: stored.refreshToken,
      }),
    })
    if (!response.ok) return null
    const tokens = storeTokens(await response.json())
    return tokens.accessToken
  } catch {
    return null
  }
}

// ─── Auth flow ────────────────────────────────────────────────────────────────

export function redirectToLogin(): void {
  const config = getCognitoConfig()
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes.join(" "),
    identity_provider: config.identityProvider,
  })
  window.location.href = `https://${config.domain}/oauth2/authorize?${params}`
}

export async function handleCallback(code: string): Promise<StoredTokens> {
  const config = getCognitoConfig()
  const response = await fetch(`https://${config.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
    }),
  })

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`)
  }

  return storeTokens(await response.json())
}

export function logout(): void {
  sessionStorage.removeItem(TOKEN_KEY)
  const config = getCognitoConfig()
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri,
  })
  window.location.href = `https://${config.domain}/logout?${params}`
}

// ─── JWT decoding ─────────────────────────────────────────────────────────────

/**
 * Safely decodes a JWT payload, handling base64url encoding (RFC 4648 §5)
 * which replaces '\u002b' with '-' and '/' with '_' and omits '=' padding.
 * Plain atob() throws a DOMException when these characters appear in the
 * payload — typically in email addresses, UUIDs, or user pool identifiers.
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const raw = token.split(".")[1]
  const base64 = raw
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(raw.length / 4) * 4, "=")
  // atob yields a binary string whose code points are only reliable up to 0x7f
  // (MDN Base64 "The Unicode problem"); decode the bytes as UTF-8 so non-ASCII
  // claims (accented/CJK operator names) are not corrupted.
  const bytes = Uint8Array.from(atob(base64), (ch) => ch.charCodeAt(0))
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>
}

// ─── User object ───────────────────────────────────────────────────────────────

/**
 * Parse the ID token and return an application-level user object.
 *
 * For the POC we seed facilities and permissions from a hardcoded mapping
 * until IhmRBAC is wired up. When we migrate to Federate + RBAC, this is
 * the one function that changes: facilities and permissions will come from
 * the token's custom claims.
 *
 * POC demo shortcut: when VITE_SKIP_AUTH=true is set at build time, skip
 * the token lookup and return a fully-permissioned demo user. This lets
 * us hand out the HHU URL to demo audiences without threading them
 * through Cognito/Midway sign-in.
 */
export function getCurrentUser(): AuthenticatedUser | null {
  const skipAuth = (import.meta.env.VITE_SKIP_AUTH as string | undefined) === "true"
  if (skipAuth) {
    return {
      alias: "eclipse-demo",
      name: "Eclipse Demo",
      email: "eclipse-demo@amazon.com",
      facilities: ["KCT", "KMD", "KNY"],
      permissions: [PERMISSION.PO_READ, PERMISSION.RECEIPT_CREATE, PERMISSION.RECEIPT_READ],
    }
  }
  const tokens = getStoredTokens()
  if (!tokens) return null
  try {
    const payload = decodeJwtPayload(tokens.idToken)
    const alias = (payload.email as string | undefined)?.split("@")[0] ?? "unknown"
    const name = (payload.name as string | undefined) ?? alias
    return {
      alias,
      name,
      email: (payload.email as string | undefined) ?? "",
      // TODO(https://sim.amazon.com/issues/ECLIPSE-001): Replace with IhmRBAC claim lookup.
      // Every authenticated user currently receives full operator permissions at all facilities.
      // This is acceptable for the POC only — production must gate on RBAC role assignments.
      facilities: ["KCT", "KMD", "KNY"],
      permissions: [PERMISSION.PO_READ, PERMISSION.RECEIPT_CREATE, PERMISSION.RECEIPT_READ],
    }
  } catch {
    return null
  }
}

export function userHasPermission(user: AuthenticatedUser | null, permission: Permission): boolean {
  return !!user && user.permissions.includes(permission)
}

export function userCanOperateAt(user: AuthenticatedUser | null, facility: string): boolean {
  return !!user && user.facilities.includes(facility)
}
